import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

interface ChangeEmailModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangeEmailModal({ open, onClose }: ChangeEmailModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ email });
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      setSuccess(true);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('changeEmail.title')}>
      {success ? (
        <div className="text-center py-4">
          <p className="text-sm font-semibold text-emerald-700 leading-relaxed">
            {t('changeEmail.success')}
          </p>
          <button
            onClick={handleClose}
            className="mt-6 w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm hover:bg-stone-800 transition-colors"
          >
            OK
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('changeEmail.newEmail')}
            className="w-full px-5 py-4 bg-stone-50 rounded-2xl border border-stone-200 text-sm font-semibold text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
          {error && <p className="text-sm font-semibold text-rose-500">{error}</p>}
          <button
            type="submit"
            disabled={loading || !email}
            className="w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm hover:bg-stone-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('changeEmail.submit')}
          </button>
        </form>
      )}
    </Modal>
  );
}
